import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

type Tier = "Micro" | "Small" | "Medium" | "Large" | "XLarge";

interface SubscriptionSummary {
  uuid: string;
  status: string;
  modules: string[];
  currentBillingTier: Tier | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
}

interface Response {
  subscriptions: SubscriptionSummary[];
  isBillingExempt: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  trialing: "Trialing",
  active: "Active",
  past_due: "Past due",
  canceled: "Canceled",
  incomplete: "Incomplete",
  incomplete_expired: "Expired",
  unpaid: "Unpaid",
  paused: "Paused",
};

const STATUS_TONE: Record<string, string> = {
  trialing: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  active: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
  past_due: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  canceled: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  incomplete: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  incomplete_expired: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  unpaid: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-200",
  paused: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
};

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

export function Billing() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const { activeOrganisation, refreshSession } = useAuth();
  const [data, setData] = useState<Response | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const checkoutResult = params.get("checkout");

  const refresh = useCallback(async () => {
    try {
      const res = await api<Response>("/billing/subscriptions");
      setData(res);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // After a Stripe Checkout success or an in-app module addition, refresh
  // the session so the sidebar picks up the new active_modules.
  useEffect(() => {
    if (checkoutResult === "success" || checkoutResult === "added") {
      void refreshSession();
      void refresh();
    }
  }, [checkoutResult, refreshSession, refresh]);

  const openPortal = async () => {
    setBusy(true);
    setErrors([]);
    try {
      const res = await api<{ url: string }>("/billing/portal", { method: "POST" });
      window.location.href = res.url;
    } catch (err) {
      setErrors(formatApiError(err));
      setBusy(false);
    }
  };

  const isAdmin = activeOrganisation?.role === "Admin";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Billing</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Subscriptions, payment methods and invoices for this organisation.
        </p>
      </div>

      {checkoutResult === "success" ? (
        <div
          role="status"
          className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
        >
          Subscription confirmed. Your trial has started — modules will appear in the sidebar in a
          moment.
        </div>
      ) : null}

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {data === null ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : data.isBillingExempt ? (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/40 dark:bg-emerald-500/10">
          <h2 className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
            Comped — managed by platform admin
          </h2>
          <p className="mt-2 text-sm text-emerald-900 dark:text-emerald-100">
            This organisation is exempt from Stripe billing. Your active modules are set manually by
            the platform admin and Stripe webhooks do not affect them.
          </p>
          {activeOrganisation && activeOrganisation.activeModules.length > 0 ? (
            <ul className="mt-3 list-disc pl-5 text-sm text-emerald-900 dark:text-emerald-100">
              {activeOrganisation.activeModules.map((m) => (
                <li key={m}>{m.replace(/_/g, " ").replace("ISO", "ISO ")}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : data.subscriptions.length === 0 ? (
        <section className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-800/40">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No subscriptions yet. Add an ISO standard to get started — each comes with a 14-day free
            trial.
          </p>
          <Link
            to={`/o/${slug}/billing/upgrade`}
            className="mt-3 inline-block rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            See standards
          </Link>
        </section>
      ) : (
        <>
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Subscriptions
            </h2>
            <ul className="mt-2 space-y-2">
              {data.subscriptions.map((sub) => {
                const tone =
                  STATUS_TONE[sub.status] ??
                  "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
                const label = STATUS_LABEL[sub.status] ?? sub.status;
                return (
                  <li
                    key={sub.uuid}
                    className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          Platform Core Fee{sub.modules.length > 0 ? " + " : ""}
                          {sub.modules.length > 0
                            ? sub.modules
                                .map((m) => m.replace(/_/g, " ").replace("ISO", "ISO "))
                                .join(", ")
                            : ""}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {sub.currentBillingTier ? `${sub.currentBillingTier} tier · ` : ""}
                          {sub.trialEnd ? `Trial ends ${formatDateUk(sub.trialEnd)} · ` : ""}
                          {sub.currentPeriodEnd
                            ? `Renews ${formatDateUk(sub.currentPeriodEnd)}`
                            : ""}
                          {sub.cancelAtPeriodEnd ? " · Will not renew" : ""}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${tone}`}
                      >
                        {label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>

          {isAdmin ? (
            <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Manage payment, invoices and cancellation
              </h2>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Opens Stripe&apos;s hosted Customer Portal in the same tab. You can update your
                card, download invoices, and cancel subscriptions from there.
              </p>
              <button
                type="button"
                onClick={() => void openPortal()}
                disabled={busy}
                className="mt-3 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {busy ? "Opening Stripe…" : "Open Stripe portal →"}
              </button>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
